import React from "react";
import Modal from "react-responsive-modal";
import { Button, FormControl, FormGroup, ControlLabel } from "react-bootstrap";

export const MAX_METADATA_TITLE_CHARS = 120;
export const MAX_METADATA_DESCRIPTION_CHARS = 600;
export const MAX_METADATA_TRANSCRIPT_CHARS = 20000;
export const MAX_METADATA_TRANSCRIPT_VTT_CHARS = 250000;
export const TRANSCRIPT_PROVIDER_OPENAI = "openai";
export const TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE = "aws-transcribe";
export const TAGS_PROVIDER_OPENAI = "openai";
export const TAGS_PROVIDER_REKOGNITION = "rekognition";

function isMetadataGenerationInProgress(metadataGeneratingField = "", field = "") {
	const current = `${metadataGeneratingField || ""}`;
	return field ? current === field : current !== "";
}

export default function DriveMetadataEditorModal(props) {
	const {
		open,
		onClose,
		targetName,
		saving = false,
		metadataGeneratingField = "",
		title = "",
		description = "",
		transcript = "",
		transcriptVtt = "",
		primaryTags = [],
		secondaryTags = [],
		tagsInput = "",
		previewUrl = "",
		previewType = "",
		galleryItems = [],
		galleryIndex = -1,
		transcriptProvider = TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE,
		tagsProvider = TAGS_PROVIDER_OPENAI,
		onGallerySelect,
		onTitleChange,
		onDescriptionChange,
		onTranscriptChange,
		onTranscriptVttChange,
		onTagsInputChange,
		onAddTags,
		onRemovePrimaryTag,
		onPromoteSecondaryTag,
		onPromoteAllSecondaryTags,
		onTranscriptProviderChange,
		onTagsProviderChange,
		onGenerateTitle,
		onGenerateDescription,
		onGenerateTranscript,
		onGenerateTranscriptVtt,
		onGenerateTags,
		onSaveAndPrevious,
		onSaveAndNext,
		onSave
	} = props;

	const metadataActionControlHeight = 34;
	const metadataActionButtonStyle = {
		height: `${metadataActionControlHeight}px`,
		minHeight: `${metadataActionControlHeight}px`,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		whiteSpace: "nowrap",
		padding: "0 12px"
	};
	const metadataActionSelectStyle = {
		height: `${metadataActionControlHeight}px`,
		minHeight: `${metadataActionControlHeight}px`,
		marginBottom: "0px",
		paddingTop: "0px",
		paddingBottom: "0px",
		lineHeight: `${metadataActionControlHeight}px`
	};
	const anyGenerationInProgress = isMetadataGenerationInProgress(metadataGeneratingField);
	const normalizedGalleryItems = Array.isArray(galleryItems) ? galleryItems : [];
	const rawGalleryIndex = Number(galleryIndex);
	const safeGalleryIndex = normalizedGalleryItems.length > 0 && rawGalleryIndex >= 0 && rawGalleryIndex < normalizedGalleryItems.length
		? rawGalleryIndex
		: -1;
	const currentGalleryItem = safeGalleryIndex >= 0 ? normalizedGalleryItems[safeGalleryIndex] : null;
	const displayPreviewUrl = currentGalleryItem?.url || previewUrl;
	const displayPreviewType = currentGalleryItem?.type || previewType;
	const displayPreviewName = currentGalleryItem?.name || targetName || "selected file";
	const resolvedPreviewType = `${displayPreviewType || ""}`.trim().toLowerCase();
	const showImagePreview = !!displayPreviewUrl && (resolvedPreviewType === "image" || /\.(avif|bmp|gif|jpe?g|png|svg|webp)(\?.*)?$/i.test(`${displayPreviewUrl}`));
	const canNavigateGallery = showImagePreview && safeGalleryIndex >= 0 && normalizedGalleryItems.length > 1 && typeof onGallerySelect === "function";
	const galleryNavigationDisabled = saving || anyGenerationInProgress;
	const selectGalleryOffset = (offset) => {
		if (!canNavigateGallery || galleryNavigationDisabled) {
			return;
		}
		const nextIndex = (safeGalleryIndex + offset + normalizedGalleryItems.length) % normalizedGalleryItems.length;
		onGallerySelect(normalizedGalleryItems[nextIndex], nextIndex);
	};

	return (
		<Modal
			open={open}
			onClose={saving ? () => {} : onClose}
			classNames={{ overlay: "drive-metadata-modal-overlay", modal: "drive-metadata-modal" }}
			styles={{
				overlay: {
					padding: 0,
					margin: 0,
					display: "block",
					overflow: "hidden"
				},
				modal: {
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					width: "100%",
					height: "100%",
					maxWidth: "none",
					maxHeight: "none",
					margin: 0,
					padding: 0,
					borderRadius: 0,
					boxSizing: "border-box"
				}
			}}
			closeButton
		>
			<div className="drive-metadata-modal-shell">
				<div className="drive-metadata-modal-header">
					<h4>Metadata Editor</h4>
				</div>
				<div className={`drive-metadata-modal-content ${showImagePreview ? "has-preview" : ""}`}>
					{showImagePreview && (
						<div className="drive-metadata-modal-preview">
							<div className="drive-metadata-modal-preview-stage">
								{canNavigateGallery && (
									<Button
										className="drive-metadata-modal-gallery-button previous"
										onClick={() => selectGalleryOffset(-1)}
										disabled={galleryNavigationDisabled}
										title="Previous image"
									>
										&lt;
									</Button>
								)}
								<img
									alt={displayPreviewName}
									src={displayPreviewUrl}
								/>
								{canNavigateGallery && (
									<Button
										className="drive-metadata-modal-gallery-button next"
										onClick={() => selectGalleryOffset(1)}
										disabled={galleryNavigationDisabled}
										title="Next image"
									>
										&gt;
									</Button>
								)}
							</div>
							<div className="drive-metadata-modal-preview-meta">
								<div className="drive-metadata-modal-preview-name" title={displayPreviewName}>
									{displayPreviewName}
								</div>
								{canNavigateGallery && (
									<div className="drive-metadata-modal-preview-count">
										{safeGalleryIndex + 1} / {normalizedGalleryItems.length}
									</div>
								)}
							</div>
						</div>
					)}
					<div className="drive-metadata-modal-form">
					<div style={{ marginBottom: "10px", color: "#5f6f82" }}>
						Manage title, description, transcript text, and searchable tags for <b>{targetName || "selected file"}</b>.
					</div>

					<FormGroup controlId="shared-metadata-title">
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
							<ControlLabel style={{ marginBottom: 0 }}>Title</ControlLabel>
							{onGenerateTitle && (
								<Button
									style={metadataActionButtonStyle}
									onClick={onGenerateTitle}
									disabled={anyGenerationInProgress || saving}
								>
									{isMetadataGenerationInProgress(metadataGeneratingField, "title") ? "Generating..." : "Generate Title"}
								</Button>
							)}
						</div>
						<FormControl
							type="text"
							placeholder="Short display/search title"
							value={title}
							onChange={onTitleChange}
						/>
						<div style={{ marginTop: "4px", color: "#8b97a6", fontSize: "12px" }}>
							{`${title || ""}`.length}/{MAX_METADATA_TITLE_CHARS}
						</div>
					</FormGroup>

					<FormGroup controlId="shared-metadata-description">
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
							<ControlLabel style={{ marginBottom: 0 }}>Description</ControlLabel>
							{onGenerateDescription && (
								<Button
									style={metadataActionButtonStyle}
									onClick={onGenerateDescription}
									disabled={anyGenerationInProgress || saving}
								>
									{isMetadataGenerationInProgress(metadataGeneratingField, "description") ? "Generating..." : "Generate Description"}
								</Button>
							)}
						</div>
						<FormControl
							componentClass="textarea"
							rows={3}
							placeholder="Concise description for search/discovery"
							value={description}
							onChange={onDescriptionChange}
						/>
						<div style={{ marginTop: "4px", color: "#8b97a6", fontSize: "12px" }}>
							{`${description || ""}`.length}/{MAX_METADATA_DESCRIPTION_CHARS}
						</div>
					</FormGroup>

					<FormGroup controlId="shared-metadata-transcript">
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", gap: "8px", flexWrap: "wrap" }}>
							<ControlLabel style={{ marginBottom: 0 }}>Transcript / Content Text</ControlLabel>
							<div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
								{onTranscriptProviderChange && (
									<FormControl
										componentClass="select"
										value={transcriptProvider}
										onChange={onTranscriptProviderChange}
										disabled={anyGenerationInProgress || saving}
										style={{ width: "180px", ...metadataActionSelectStyle }}
									>
										<option value={TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE}>Amazon Transcribe</option>
										<option value={TRANSCRIPT_PROVIDER_OPENAI}>OpenAI</option>
									</FormControl>
								)}
								{onGenerateTranscript && (
									<Button
										style={metadataActionButtonStyle}
										onClick={onGenerateTranscript}
										disabled={anyGenerationInProgress || saving}
									>
										{isMetadataGenerationInProgress(metadataGeneratingField, "transcript") ? "Generating..." : "Generate Transcript"}
									</Button>
								)}
								{onGenerateTranscriptVtt && (
									<Button
										style={metadataActionButtonStyle}
										onClick={onGenerateTranscriptVtt}
										disabled={anyGenerationInProgress || saving}
									>
										{isMetadataGenerationInProgress(metadataGeneratingField, "transcriptVtt") ? "Generating..." : "Generate VTT"}
									</Button>
								)}
							</div>
						</div>
						<FormControl
							componentClass="textarea"
							rows={5}
							placeholder="Long-form text representation (transcript, narration, image content notes, etc.)"
							value={transcript}
							onChange={onTranscriptChange}
						/>
						<div style={{ marginTop: "4px", color: "#8b97a6", fontSize: "12px" }}>
							{`${transcript || ""}`.length}/{MAX_METADATA_TRANSCRIPT_CHARS}
						</div>
					</FormGroup>

					<FormGroup controlId="shared-metadata-vtt">
						<ControlLabel>Timed Transcript (VTT)</ControlLabel>
						<FormControl
							componentClass="textarea"
							rows={6}
							placeholder="WEBVTT content for timed captions/subtitles."
							value={transcriptVtt}
							onChange={onTranscriptVttChange}
						/>
						<div style={{ marginTop: "4px", color: "#8b97a6", fontSize: "12px" }}>
							{`${transcriptVtt || ""}`.length}/{MAX_METADATA_TRANSCRIPT_VTT_CHARS}
						</div>
					</FormGroup>

					<FormGroup controlId="shared-metadata-tags-provider">
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
							<ControlLabel style={{ marginBottom: 0 }}>Tags</ControlLabel>
							<div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
								{onTagsProviderChange && (
									<FormControl
										componentClass="select"
										value={tagsProvider}
										onChange={onTagsProviderChange}
										disabled={anyGenerationInProgress || saving}
										style={{ width: "190px", ...metadataActionSelectStyle }}
									>
										<option value={TAGS_PROVIDER_OPENAI}>OpenAI</option>
										<option value={TAGS_PROVIDER_REKOGNITION}>AWS Rekognition</option>
									</FormControl>
								)}
								{onGenerateTags && (
									<Button
										style={metadataActionButtonStyle}
										onClick={onGenerateTags}
										disabled={anyGenerationInProgress || saving}
									>
										{isMetadataGenerationInProgress(metadataGeneratingField, "tags") ? "Generating..." : "Generate Tags"}
									</Button>
								)}
							</div>
						</div>
					</FormGroup>

					<FormGroup controlId="shared-metadata-add-tags">
						<ControlLabel>Add Primary Tags</ControlLabel>
						<div style={{ display: "flex", gap: "8px" }}>
							<FormControl
								type="text"
								placeholder="Add one or more tags (comma separated)"
								value={tagsInput}
								onChange={onTagsInputChange}
								onKeyDown={(event) => {
									if (event.key === "Enter") {
										event.preventDefault();
										if (onAddTags) {
											onAddTags();
										}
									}
								}}
							/>
							<Button
								bsStyle="primary"
								style={metadataActionButtonStyle}
								onClick={onAddTags}
								disabled={saving || anyGenerationInProgress}
							>
								Add
							</Button>
						</div>
					</FormGroup>

					<div style={{ marginTop: "10px" }}>
						<ControlLabel>Primary Tags</ControlLabel>
						{primaryTags.length === 0 ? (
							<div style={{ color: "#8b97a6" }}><i>No primary tags yet.</i></div>
						) : (
							<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
								{primaryTags.map((tag) => (
									<span
										key={`shared-primary-${tag}`}
										style={{
											display: "inline-flex",
											alignItems: "center",
											gap: "6px",
											padding: "4px 8px",
											borderRadius: "999px",
											backgroundColor: "#e9f3ff",
											color: "#1f4d88",
											fontSize: "12px"
										}}
									>
										{tag}
										<Button
											bsSize="xsmall"
											onClick={() => onRemovePrimaryTag && onRemovePrimaryTag(tag)}
											style={{ lineHeight: "1", padding: "1px 5px" }}
											disabled={saving}
										>
											×
										</Button>
									</span>
								))}
							</div>
						)}
					</div>

					<div style={{ marginTop: "16px" }}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
							<ControlLabel style={{ marginBottom: 0 }}>Secondary Suggested Tags</ControlLabel>
							<Button
								style={metadataActionButtonStyle}
								onClick={onPromoteAllSecondaryTags}
								disabled={secondaryTags.length === 0 || anyGenerationInProgress}
							>
								Promote All
							</Button>
						</div>
						{secondaryTags.length === 0 ? (
							<div style={{ color: "#8b97a6" }}><i>No secondary tags found.</i></div>
						) : (
							<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
								{secondaryTags.map((tag) => (
									<Button
										key={`shared-secondary-${tag}`}
										bsSize="small"
										onClick={() => onPromoteSecondaryTag && onPromoteSecondaryTag(tag)}
										style={{
											borderRadius: "999px",
											padding: "4px 10px",
											backgroundColor: "#f5f7fa",
											border: "1px solid #d8dee8",
											color: "#34495e"
										}}
									>
										+ {tag}
									</Button>
								))}
							</div>
						)}
					</div>
				</div>
				</div>
				<div className="modal-footer-container drive-metadata-modal-footer">
					<div className="drive-metadata-modal-footer-gallery">
						{canNavigateGallery && onSaveAndPrevious && (
							<Button
								style={metadataActionButtonStyle}
								onClick={onSaveAndPrevious}
								disabled={saving || anyGenerationInProgress}
							>
								Save &amp; Previous
							</Button>
						)}
						{canNavigateGallery && onSaveAndNext && (
							<Button
								style={metadataActionButtonStyle}
								onClick={onSaveAndNext}
								disabled={saving || anyGenerationInProgress}
							>
								Save &amp; Next
							</Button>
						)}
					</div>
					<div className="drive-metadata-modal-footer-main">
						<Button onClick={onClose} style={{ ...metadataActionButtonStyle, marginRight: "10px" }} disabled={saving}>
							Cancel
						</Button>
						<Button
							bsStyle="primary"
							style={metadataActionButtonStyle}
							onClick={onSave}
							disabled={saving || anyGenerationInProgress}
						>
							{saving ? "Saving..." : "Save Metadata"}
						</Button>
					</div>
				</div>
			</div>
		</Modal>
	);
}
